import { useState, useEffect } from 'react';
import moment from 'moment';

import type { Moment } from 'moment';

interface ValidateDurationProps {
  startDate?: string | Date | Moment | null;
  duration?: number | null;
}

/**
 * This hook validates if a session has met its minimum duration requirements.
 * It handles both 15-minute and other session durations, with a 7-minute buffer for all sessions except 15-minute sessions.
 * It also schedules updates to the validity state when the session is not yet valid.
 */

export const useValidateDuration = ({ startDate, duration }: ValidateDurationProps): boolean => {
  const [isValid, setIsValid] = useState<boolean>(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const startTime = startDate ? moment(startDate) : null;

    // Keep previous behavior: missing/invalid values are treated as valid.
    if (!startTime?.isValid() || duration == null || duration <= 0) {
      setIsValid(true);
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }

    // Calculate current duration and required minutes for validation.
    const actualDuration = moment().diff(startTime, 'minutes');
    const durationInMinutes = duration / 60;
    const requiredMinutes = durationInMinutes - 7; // 7-minute buffer for all sessions except 15min

    // Calculate current validity.
    const currentValid =
      durationInMinutes === 15 ? actualDuration >= 0 : actualDuration > requiredMinutes;
    setIsValid(currentValid);

    // If not valid, schedule an update for when it will become valid.
    if (!currentValid) {
      const msUntilRequired = Math.max((requiredMinutes - actualDuration) * 60 * 1000, 0);
      timeoutId = setTimeout(() => {
        setIsValid(true);
      }, msUntilRequired);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [startDate, duration]);

  return isValid;
};
